import { useEffect, useState, useCallback } from "react";
import { Modal, Spin, Table, Tooltip } from "antd";
import { Download, Eye } from "lucide-react";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import {
    fetchLatestApprovalCertificateList as fetchApprovalCertificateFromApi,
    downloadApprovalCertificate as downloadApprovalCertificateFromApi
} from "../../services/documentApi";
import { getAccessToken } from "../../services/authApi";
import { checkLoanResourceLock, fieldReader } from "../../services/loanApi";
import { generateCorrespondentAdditionalDocuments } from "./correspondentCertificateHelper"
import { hasPageAccess } from "../../services/personaApi";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/legacy/build/pdf.worker.min.mjs";
pdfjsLib.GlobalWorkerOptions.workerSrc = "";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

export default function CorrespondentCertificatePage() {
    const [loanId, setLoanId] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success");

    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [approvalAttachments, setApprovalAttachments] = useState([]); // array from backend
    const [currentMilestone, setCurrentMilestone] = useState("");
    const [milestones, setMilestones] = useState([]);

    const allowedMilestones = [
        // "Submitted to UW",
        // "Initial Approval",
        // "Submit for Condition Review",
        // "Conditional Approval",
        // "Submit for Final Review",
        // "CTC", 
        // "Registered",
        // "Started",
        // "Setup"
    ];

    const isMilestoneDone = (milestoneName) => {
        if (!milestoneName) return false;

        const normalizedCurrent = milestoneName.toLowerCase();

        return (
            allowedMilestones?.some(
                name => name.toLowerCase() === normalizedCurrent
            ) &&
            milestones?.some(
                m =>
                    m.name?.toLowerCase() === normalizedCurrent &&
                    m.doneIndicator === true
            )
        );
    };

    const canCleartoCloseConditional = isMilestoneDone(currentMilestone);
    const canLoanApprovalConditional = isMilestoneDone(currentMilestone);
    const canPurchaseSuspenseConditional = isMilestoneDone(currentMilestone);

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewTitle, setPreviewTitle] = useState("");
    const [pdfPages, setPdfPages] = useState([]);
    const [loanLocked, setLoanLocked] = useState(false);
    const [lockedByUser, setLockedByUser] = useState("");
    const [loanType, setLoanType] = useState("");
    const [fieldValues, setFieldValues] = useState({});
    const [channel, setChannel] = useState("");

    const [canGenerate, setCanGenerate] = useState(true); // like canSave
    const [permissionMessage, setPermissionMessage] = useState("");
    const [initializing, setInitializing] = useState(true);

    useEffect(() => {
        const boot = async () => {
            // 1) loan
            try {
                if (window.elli?.script?.guest) {
                    window.elli.script.guest.create();
                    const loanObject = await window.elli.script.getObject("loan");
                    const loan = await loanObject.all();

                    setMilestones(loan?.loan?.milestones);
                    setLoanId(loan?.loan?.id || "");

                    const authObj = await window.elli.script.getObject("auth");
                    const authCode = await authObj.createAuthCode();
                    const token = await getAccessToken(authCode);

                    const fields = ["2626", "Log.MS.CurrentMilestone", "CX.LOAN.TYPE", "CX.LOAN.PROD.BROKER.SELECT", "CX.DOC.TYPE"];

                    const data = await fieldReader(token, fields, loan?.loan?.id);

                    setCurrentMilestone(data["Log.MS.CurrentMilestone"]);
                    setLoanType(data["CX.LOAN.TYPE"] || "");
                    setChannel(data["2626"] || "");

                    setFieldValues(data);

                } else {
                    console.warn("elli.script not available");
                }
            } catch (err) {
                console.error("Failed to read loan id from elli script", err);
            } finally {
                setInitializing(false);
            }


            // 2) auth + persona gating
            try {
                const authObj = await window.elli.script.getObject("auth");
                const user = await authObj.getUser();
                console.log("Current user:", user);
                // const allowed = await hasPageAccess("ApprovalCertificatePage", user);
                const allowed = true; // for now allow all without persona
                setCanGenerate(allowed);

                if (!allowed) {
                    setPermissionMessage(
                        "Allow Generate only for Underwriting Persona; other personas will be view-only."
                    );
                } else {
                    setPermissionMessage("");
                }
            } catch (e) {
                console.warn("Failed to read auth user:", e);
                setCanGenerate(false);
                setPermissionMessage(
                    "Allow Generate only for Underwriting Persona; other personas will be view-only."
                );
            }
        };

        boot();
    }, []);


    // when loanId changes fetch attachments
    useEffect(() => {
        if (loanId && channel?.toLowerCase() === "correspondent") {
            fetchLatestCorrespondentCertificate();
            checkIfLoanLocked();
        }
    }, [loanId, channel]);

    const showToast = (message, type = "success") => {
        setToastMessage(message);
        setToastType(type);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 5000);
    };

    // call the generate endpoint you had earlier
    const callGenerateApi = async (docType) => {
        if (!canGenerate) {
            showToast(permissionMessage || "You don’t have permission to generate.", "error");
            return;
        }

        if (loanLocked) {
            showToast(`This loan is locked by '${lockedByUser}'. You cannot generate.`, "error");
            return;
        }

        setLoading(true);
        let fileName = "";
        if (docType.toLowerCase() === "cleartoclose") {
            fileName = "Correspondent Clear to Close.pdf";
        } else if (docType.toLowerCase() === "loanapproval") {
            fileName = "Correspondent Loan Approval.pdf";
        } else if (docType.toLowerCase() === "purchasesuspense") {
            fileName = "Correspondent Purchase Suspense.pdf";
        }
        setLoadingText(`Generating ${docType} letter...`);
        try {
            if (!loanId) {
                showToast("Loan ID not found. Cannot generate correspondent letter.", "error");
                return;
            }
            const authObj = await window.elli.script.getObject("auth");
            const authCode = await authObj.createAuthCode();
            const endpoint = `${API_DOMAIN}/api/CorrespondentLetters/generate`;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    loanId,
                    authCode,
                    docType
                })
            });

            if (!response.ok) {
                const text = await response.text().catch(() => response.statusText || "Unknown error");
                console.error("Correspondent Letter API error", response.status, text);
                showToast(`Failed to generate ${docType} letter: ${response.status} - ${text}`, "error");
            } else {

                showToast(`Correspondent Letter "${fileName}" generated successfully.`, "success");

                await fetchLatestCorrespondentCertificate();
            }
        } catch (err) {
            console.error("Error generating correspondent letter", err);
            showToast(`Error: ${err?.message || err}`, "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    // fetch attachments via the service wrapper
    const fetchLatestCorrespondentCertificate = useCallback(async () => {
        if (!loanId) return;
        setAttachmentsLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            // service call (uses axios under the hood)
            const result = await fetchApprovalCertificateFromApi(authCode, loanId);

            if (Array.isArray(result)) {
                setApprovalAttachments(result);
            } else {
                setApprovalAttachments(result ? [result] : []);
            }

        } catch (err) {
            const errorCode = logError("FETCH_ATTACHMENT_ERROR");
            logAppInsightError("Error fetching Correspondent Letter", {
                component: "CorrespondentCertificatePage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to get Correspondent Letter.`
            });
            console.error("fetchLatestCorrespondentLetter error:", err);
            showToast("Failed to fetch correspondent letter attachments.", "error");
        } finally {
            setAttachmentsLoading(false);
        }
    }, [loanId]);

    // download via service wrapper
    const downloadApprovalCertificate = useCallback(
        async (attachmentId, filename, type) => {
            try {
                if (!loanId) {
                    showToast("Loan ID not found. Cannot download correspondent letter.", "error");
                    return;
                }
                if (!attachmentId) {
                    showToast("No document found to download.", "error");
                    return;
                }
                const tpoAuthObject = await window.elli.script.getObject("auth");
                const authCode = await tpoAuthObject.createAuthCode();
                const blob = await downloadApprovalCertificateFromApi(authCode, loanId, attachmentId, type);
                // blob is returned from axios as response.data (blob)
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = filename || "document.pdf";
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                const errorCode = logError("DOWNLOAD_ATTACHMENT_ERROR");
                logAppInsightError("Download Correspondent Letter failed", {
                    component: "CorrespondentCertificatePage",
                    problemId: errorCode,
                    errorCode,
                    rawError: err,
                    errorMessage: `${errorCode} - Attachment download failed.`
                });
                console.error("downloadCorrespondentLetter error:", err);
                showToast("Failed to download correspondent letter.", "error");
            }
        },
        [loanId]
    );

    // table columns (equal widths for main three)
    const columns = [
        {
            title: "Document",
            dataIndex: "document",
            key: "document",
            width: "33%"
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: "33%"
        },
        {
            title: "Created Date (Pacific)",
            dataIndex: "createdDatePacific",
            key: "createdDatePacific",
            width: "34%",
            render: (val) => (val ? val : "")
        }
    ];

    const findAttachment = (matchTitlePart) => {
        let matchTitle = "";
        if (matchTitlePart.toLowerCase() === "cleartoclose") {
            matchTitle = "Correspondent Clear to Close";
        } else if (matchTitlePart.toLowerCase() === "loanapproval") {
            matchTitle = "Correspondent Loan Approval";
        } else if (matchTitlePart.toLowerCase() === "purchasesuspense") {
            matchTitle = "Correspondent Purchase Suspense";
        } else {
            matchTitle = matchTitlePart;
        }
        return approvalAttachments.find(
            (a) => (a.title || "").toLowerCase().includes(matchTitle.toLowerCase())
        );
    };

    const cleartocloseAttachment = findAttachment("cleartoclose");
    const loanapprovalAttachment = findAttachment("loanapproval");
    const purchaseSuspenseAttachment = findAttachment("purchasesuspense");

    const buildRowFromAttachment = (att, fallbackKey) => {
        if (!att) {
            return {
                key: fallbackKey,
                document: "",
                title: "No document found",
                createdDatePacific: ""
            };
        }

        const createdDatePacific =
            att.createdDatePacific ||
            (att.createdDateUtc ? new Date(att.createdDateUtc).toLocaleString("en-US", { timeZone: "America/Los_Angeles" }) : "-");

        return {
            key: att.id,
            document: att.document || att.assignedToEntityType || att.assignedTo?.entityType || att.assignedTo?.entityName || "-",
            title: att.title || "-",
            createdDatePacific
        };
    };

    const cleartocloseRow = buildRowFromAttachment(cleartocloseAttachment, "cleartoclose-none");
    const loanapprovalRow = buildRowFromAttachment(loanapprovalAttachment, "loanapproval-none");
    const purchaseSuspenseRow = buildRowFromAttachment(purchaseSuspenseAttachment, "purchase-suspense-none");


    const renderPDF = async (blobUrl) => {
        const loadingTask = pdfjsLib.getDocument({
            url: blobUrl,
            disableWorker: true
        });

        const pdf = await loadingTask.promise;

        const pageList = [];
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 1.2 });

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({ canvasContext: ctx, viewport }).promise;

            pageList.push(canvas.toDataURL());
        }

        return pageList;
    };

    const previewCertificate = async (attachmentId, type) => {
        try {
            setPreviewLoading(true);
            setPreviewVisible(true);
            setPdfPages([]);

            setPreviewTitle(
                type === "cleartoclose"
                    ? "Correspondent Clear to Close Letter"
                    : type === "loanapproval"
                        ? "Loan Approval Letter"
                        : "Purchase Suspense Letter"
            );

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const blob = await downloadApprovalCertificateFromApi(
                authCode, loanId, attachmentId, type
            );

            const blobUrl = URL.createObjectURL(blob);

            const pages = await renderPDF(blobUrl);
            setPdfPages(pages);

            setPreviewVisible(true);
        } catch (err) {
            console.error("Preview error:", err);
            showToast("Unable to preview document.", "error");
        } finally {
            setPreviewLoading(false);
        }
    };

    const previewGeneratedPdf = async (docType) => {
        try {
            setPreviewLoading(true);
            setPreviewVisible(true);
            setPdfPages([]);

            setPreviewTitle(
                docType === "cleartoclose"
                    ? "Correspondent Clear to Close Letter (Preview Only)"
                    : docType === "loanapproval"
                        ? "Loan Approval Letter (Preview Only)"
                        : "Purchase Suspense Letter (Preview Only)"
            );

            if (!loanId) {
                showToast("Loan ID not found. Cannot preview correspondent letter.", "error");
                return;
            }

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const endpoint = `${API_DOMAIN}/api/CorrespondentLetters/preview-pdf-only`;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ loanId, authCode, docType })
            });

            if (!response.ok) {
                const errText = await response.text();
                showToast(`Failed to preview PDF: ${errText}`, "error");
                return;
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Use your existing renderPDF() method
            const pages = await renderPDF(blobUrl);
            setPdfPages(pages);

        } catch (err) {
            console.error("Preview-only error:", err);
            showToast("Unable to preview PDF.", "error");
        } finally {
            setPreviewLoading(false);
        }
    };

    const checkIfLoanLocked = async () => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const token = await getAccessToken(authCode);

            const result = await checkLoanResourceLock(token, loanId);

            if (result.locked) {
                setLoanLocked(true);
                setLockedByUser(result.userId || result.fullName || "Another user");
            } else {
                setLoanLocked(false);
                setLockedByUser("");
            }
        } catch (err) {
            console.error("Lock check error:", err);
        }
    };

    if (initializing) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Spin size="large" />
            </div>
        );
    }
    // Restricted access check
    if (channel?.toLowerCase() !== "correspondent") {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
                <div className="bg-white shadow-md rounded-lg p-8 max-w-lg text-center border border-gray-200 relative -mt-20">
                    {/* Error Icon */}
                    <div className="flex justify-center mb-4">
                        <svg
                            className="w-12 h-12 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L4.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>

                    <h2 className="text-xl font-semibold text-gray-800 mb-3">
                        Restricted Access
                    </h2>
                    <p className="text-gray-600">
                        This page is available only for{" "}
                        <span className="font-medium text-[#006aa9]">
                            Correspondent Loans
                        </span>
                        . Please navigate back to continue with your loan processing.
                    </p>
                </div>
            </div>
        );
    }


    return (
        <div className="space-y-10 p-25px">
            <div className="bg-[var(--white)] rounded-lg p-3 space-y-6 max-w-8xl mx-auto">

                <LoadingOverlay loading={loading} text={loadingText} />

                <div className="mt-6">
                    {attachmentsLoading ? (
                        <div className="flex justify-center items-center h-40">
                            <Spin size="large" />
                        </div>
                    ) : (
                        <>
                            {loanLocked && (
                                <div className="lock-banner">
                                    <div className="lock-message">

                                        {/* ICON */}
                                        <div className="lock-icon-wrapper">
                                            <span className="lock-icon">!</span>
                                        </div>

                                        <span>
                                            This loan is currently locked by '{lockedByUser}', so you cannot generate correspondent letters.
                                        </span>
                                    </div>

                                    <div className="lock-close" onClick={() => setLoanLocked(false)}>
                                        ×
                                    </div>
                                </div>
                            )}


                            <div className="space-y-6">
                                {/* Clear to close Panel */}
                                <div className="border rounded-lg shadow-sm bg-white">
                                    <div className="flex items-center justify-between px-4 py-3 border-b">
                                        <div className="text-lg font-medium text-gray-800">Clear to Close Letter</div>

                                        <div className="flex items-center gap-3">

                                            {/* PREVIEW ONLY BUTTON */}
                                            <Tooltip title="Preview only">
                                                <button
                                                    className={`btn-preview text-white bg-[#2080C3] hover:bg-[#5DA5D9]`}
                                                    // ${canCleartoCloseConditional
                                                    //     ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                    //     : "btn-custom-disabled"}`}
                                                    onClick={() => previewGeneratedPdf("cleartoclose")}
                                                    disabled={!canGenerate || loading} //|| !canCleartoCloseConditional
                                                >
                                                    Preview
                                                </button>
                                            </Tooltip>

                                            {/* GENERATE BUTTON */}
                                            <Tooltip title="Generate Letter">
                                                <button
                                                    className={`btn-custom rounded px-4 py-2 font-semibold text-white bg-[#2080C3] hover:bg-[#5DA5D9]`}
                                                    // ${canCleartoCloseConditional
                                                    //             ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                    //             : "btn-custom-disabled"}`}
                                                    onClick={() => callGenerateApi("cleartoclose")}
                                                    disabled={!canGenerate || loading || loanLocked} //|| !canCleartoCloseConditional 
                                                >
                                                    Generate
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        {(!cleartocloseAttachment || !cleartocloseAttachment.id) ? (
                                            <div className="py-8 px-4 text-center text-gray-600">
                                                <div className="text-base font-medium mb-2">
                                                    {/* Generate Clear to Close Letter button will be enabled only when the current milestone is{" "}
                                                    {allowedMilestones?.join(", ")} and the milestone is marked as completed. */}
                                                </div>
                                                Generate Clear to Close Letter button will be enabled all milestones.
                                            </div>
                                        ) : (
                                            (!cleartocloseAttachment || !cleartocloseAttachment.id) ? (
                                                <div className="py-8 px-4 text-center text-gray-600">
                                                    <div className="text-base font-medium mb-2">
                                                        Clear to Close letter has not been generated.
                                                    </div>
                                                </div>
                                            ) : (
                                                <Table
                                                    size="small"
                                                    columns={[
                                                        ...columns,
                                                        {
                                                            title: "Actions",
                                                            key: "actions",
                                                            width: "10%",
                                                            render: () => (
                                                                <div className="flex items-center gap-4">
                                                                    {/* Preview */}
                                                                    <Tooltip title="Preview PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                previewCertificate(
                                                                                    cleartocloseAttachment.id,
                                                                                    "cleartoclose"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Eye size={18} />
                                                                        </button>
                                                                    </Tooltip>

                                                                    {/* Download */}
                                                                    <Tooltip title="Download PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                downloadApprovalCertificate(
                                                                                    cleartocloseAttachment.id,
                                                                                    "Clear_to_Close_Letter.pdf",
                                                                                    "cleartoclose"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Download size={18} />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            )
                                                        }

                                                    ]}
                                                    dataSource={[cleartocloseRow]}
                                                    pagination={false}
                                                    bordered
                                                />
                                            )
                                        )}

                                    </div>
                                </div>

                                {/* Loan Approval Panel */}
                                <div className="border rounded-lg shadow-sm bg-white">
                                    <div className="flex items-center justify-between px-4 py-3 border-b">
                                        <div className="text-lg font-medium text-gray-800">Loan Approval Letter</div>

                                        <div className="flex items-center gap-3">

                                            {/* PREVIEW ONLY BUTTON */}
                                            <Tooltip title="Preview only">
                                                <button
                                                    className={`btn-preview text-white bg-[#2080C3] hover:bg-[#5DA5D9]`}
                                                    onClick={() => previewGeneratedPdf("loanapproval")}
                                                    disabled={!canGenerate || loading}
                                                >
                                                    Preview
                                                </button>
                                            </Tooltip>

                                            {/* GENERATE BUTTON */}
                                            <Tooltip title="Generate Letter">
                                                <button
                                                    className={`btn-custom rounded px-4 py-2 font-semibold text-white bg-[#2080C3] hover:bg-[#5DA5D9]`}
                                                    onClick={() => callGenerateApi("loanapproval")}
                                                    disabled={!canGenerate || loading || loanLocked}
                                                >
                                                    Generate
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        {(!loanapprovalAttachment || !loanapprovalAttachment.id) ? (
                                            <div className="py-8 px-4 text-center text-gray-600">
                                                <div className="text-base font-medium mb-2">
                                                    {/* Generate Loan Approval Letter button will be enabled only when the current milestone is{" "}
                                                    {allowedMilestones?.join(", ")} and the milestone is marked as completed. */}
                                                    Generate Loan Approval Letter button will be enabled all milestones.
                                                </div>
                                            </div>
                                        ) : (
                                            (!loanapprovalAttachment || !loanapprovalAttachment.id) ? (
                                                <div className="py-8 px-4 text-center text-gray-600">
                                                    <div className="text-base font-medium mb-2">
                                                        Loan approval letter has not been generated.
                                                    </div>
                                                </div>
                                            ) : (
                                                <Table
                                                    size="small"
                                                    columns={[
                                                        ...columns,
                                                        {
                                                            title: "Actions",
                                                            key: "actions",
                                                            width: "10%",
                                                            render: () => (
                                                                <div className="flex items-center gap-4">
                                                                    {/* Preview */}
                                                                    <Tooltip title="Preview PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                previewCertificate(
                                                                                    loanapprovalAttachment.id,
                                                                                    "loanapproval"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Eye size={18} />
                                                                        </button>
                                                                    </Tooltip>

                                                                    {/* Download */}
                                                                    <Tooltip title="Download PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                downloadApprovalCertificate(
                                                                                    loanapprovalAttachment.id,
                                                                                    "Loan_Approval_Letter.pdf",
                                                                                    "loanapproval"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Download size={18} />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            )
                                                        }

                                                    ]}
                                                    dataSource={[loanapprovalRow]}
                                                    pagination={false}
                                                    bordered
                                                />
                                            )
                                        )}

                                    </div>
                                </div>

                                {/* Purchase Suspense Panel */}
                                <div className="border rounded-lg shadow-sm bg-white">
                                    <div className="flex items-center justify-between px-4 py-3 border-b">
                                        <div className="text-lg font-medium text-gray-800">Purchase Suspense Letter</div>

                                        <div className="flex items-center gap-3">

                                            {/* PREVIEW ONLY BUTTON */}
                                            <Tooltip title="Preview only">
                                                <button
                                                    className={`btn-preview text-white bg-[#2080C3] hover:bg-[#5DA5D9]`}
                                                    onClick={() => previewGeneratedPdf("purchasesuspense")}
                                                    disabled={!canGenerate || loading}
                                                >
                                                    Preview
                                                </button>
                                            </Tooltip>

                                            {/* GENERATE BUTTON */}
                                            <Tooltip title="Generate Letter">
                                                <button
                                                    className={`btn-custom rounded px-4 py-2 font-semibold text-white bg-[#2080C3] hover:bg-[#5DA5D9]`}
                                                    onClick={() => callGenerateApi("purchasesuspense")}
                                                    disabled={!canGenerate || loading || loanLocked}
                                                >
                                                    Generate
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        {(!purchaseSuspenseAttachment || !purchaseSuspenseAttachment.id) ? (
                                            <div className="py-8 px-4 text-center text-gray-600">
                                                <div className="text-base font-medium mb-2">
                                                    {/* Generate Purchase Suspense Letter button will be enabled only when the current milestone is{" "}
                                                    {allowedMilestones?.join(", ")} and the milestone is marked as completed. */}
                                                    Generate Purchase Suspense Letter button will be enabled all milestones.
                                                </div>
                                            </div>
                                        ) : (
                                            (!purchaseSuspenseAttachment || !purchaseSuspenseAttachment.id) ? (
                                                <div className="py-8 px-4 text-center text-gray-600">
                                                    <div className="text-base font-medium mb-2">
                                                        Purchase Suspense Letter has not been generated.
                                                    </div>
                                                </div>
                                            ) : (
                                                <Table
                                                    size="small"
                                                    columns={[
                                                        ...columns,
                                                        {
                                                            title: "Actions",
                                                            key: "actions",
                                                            width: "10%",
                                                            render: () => (
                                                                <div className="flex items-center gap-4">
                                                                    {/* Preview */}
                                                                    <Tooltip title="Preview PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                previewCertificate(
                                                                                    purchaseSuspenseAttachment.id,
                                                                                    "purchasesuspense"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Eye size={18} />
                                                                        </button>
                                                                    </Tooltip>

                                                                    {/* Download */}
                                                                    <Tooltip title="Download PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                downloadApprovalCertificate(
                                                                                    purchaseSuspenseAttachment.id,
                                                                                    "Purchase_Suspense_Letter.pdf",
                                                                                    "purchasesuspense"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Download size={18} />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            )
                                                        }

                                                    ]}
                                                    dataSource={[purchaseSuspenseRow]}
                                                    pagination={false}
                                                    bordered
                                                />
                                            )
                                        )}

                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <Toast visible={toastVisible} type={toastType} message={toastMessage} onClose={() => setToastVisible(false)} />
            </div>
            <Modal
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width="70vw"
                centered
                styles={{
                    body: {
                        padding: 0,
                        backgroundColor: "#f5f7fa",
                        borderRadius: "8px",
                        overflow: "hidden",
                        height: "85vh",
                        overflowY: "auto"
                    },
                    header: {
                        fontSize: "18px",
                        fontWeight: 600,
                        padding: "16px 24px",
                        background: "#ffffff",
                        borderBottom: "1px solid #e5e7eb"
                    },
                    content: {
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
                    }
                }}
                title={previewTitle}
            >
                {previewLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <Spin size="large" />
                    </div>
                ) : (
                    <div className="pdf-body-container">
                        {pdfPages.map((src, index) => (
                            <img key={index} src={src} alt={`Page ${index + 1}`} className="pdf-page" />
                        ))}
                    </div>
                )}
            </Modal>
        </div>
    );


}
